import { Tooltip } from '@odo/shared-ui';
import {
    getApprovalCreditDaysLeft,
    getApprovalCreditTone,
    type ApprovalCreditTone
} from '../../utils/approvalCredit';

const TONE_CLASSES: Record<ApprovalCreditTone, string> = {
    green: 'bg-emerald-600',
    yellow: 'bg-yellow-500',
    amber: 'bg-amber-600',
    red: 'bg-rose-600',
    expired: 'bg-gray-500'
};

const TOOLTIP_COPY =
    'Approve within the credit window to get the case design fee credited off the treatment plan price.';

// The live copy would be an instruction the doctor can no longer act on: dxFees
// judges expiry as of the approval date, so a lapsed credit cannot be redeemed by
// approving now.
const EXPIRED_TOOLTIP_COPY =
    'This case design credit has expired and will no longer be credited off the treatment plan price.';

interface ApprovalCreditPillProps {
    /** ISO timestamp of case-design credit expiry, or null when there's no active credit. */
    expiresAt: string | null;
}

/** Countdown pill for the case-design-fee approval credit. Caller gates rendering on "awaiting approval". */
export function ApprovalCreditPill({ expiresAt }: ApprovalCreditPillProps) {
    if (!expiresAt) return null;

    const daysLeft = getApprovalCreditDaysLeft(expiresAt);
    const tone = getApprovalCreditTone(daysLeft);
    const hasExpired = tone === 'expired';

    const label = hasExpired
        ? 'Credit expired'
        : daysLeft === 1
          ? '1 day left'
          : `${daysLeft} days left`;

    return (
        <Tooltip content={hasExpired ? EXPIRED_TOOLTIP_COPY : TOOLTIP_COPY}>
            <span
                className={`inline-flex items-center max-w-full gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white w-fit ${TONE_CLASSES[tone]}`}
            >
                <span className="w-1.5 h-1.5 rounded-full bg-white/40 flex-shrink-0" />
                <span className="truncate">{label}</span>
            </span>
        </Tooltip>
    );
}

export default ApprovalCreditPill;
